import {
  IEvent,
  ILoadOptions,
  IVideoInfo,
  Player,
  PlayerConfig,
  Plugin as MggpPlugin,
} from 'mggp-player';
import { PlayingEntity } from './PlayingEntity';
import {
  ChannelType,
  ContentType,
  IChannel,
  IProgram,
  PlayerState,
  PLAYER_EVENT,
  PLAYER_PROPERTY,
  PluginState,
  ProgramState,
} from './types';
import { findCatchupInChannel } from './utils/findCatchupInChannel';
import { getPlayVideoFunc, PlayVideoFunc } from './utils/playVideo';
import {
  getRequestStreamFuncs,
  IRequestStreamFuncs,
} from './utils/requestStream';
import {
  subscribeOnEvents,
  unsubscribeFromEvents,
} from './utils/subscribeOnEvents';

export default class MggpPluginDvr extends MggpPlugin {
  private pluginState: PluginState = PluginState.idle;
  private currentChannel: IChannel | null = null;
  private playingEntity: PlayingEntity | null = null;
  private channelVideoInfo: IVideoInfo | null = null;
  private playVideoFunc: PlayVideoFunc;
  private requestStreamFuncs: IRequestStreamFuncs;
  private lastTimestamp = 0;
  private startCatchup: IVideoInfo | null = null;
  private isNeedToUpdateTimeoffset = false;
  private isNeedForceLive = false;

  constructor(player: Player, config: PlayerConfig) {
    super(player, config);

    this.playVideoFunc = getPlayVideoFunc(player);
    this.requestStreamFuncs = getRequestStreamFuncs(player);
    this.player.registerProperty(
      PLAYER_PROPERTY.channelState,
      this.pluginState,
      true
    );
    this.player.registerProperty(PLAYER_PROPERTY.playingProgram, null, true);
    this.player.registerProperty(
      PLAYER_PROPERTY.playingProgramDuration,
      0,
      true
    );
    this.player.registerProperty(PLAYER_PROPERTY.playingProgramTime, 0, true);
    this.player.registerProperty(PLAYER_PROPERTY.currentProgramTime, 0, true);

    this.player.on(PLAYER_EVENT.resetPlugin, this.resetPlugin);
    this.player.on(PLAYER_EVENT.contentBlocked, this.onContentBlocked);
  }

  dispose = (): void => {
    this.disablePlugin();
  };

  load = (src: string, videoInfo: IVideoInfo, options?: ILoadOptions): void => {
    if (
      videoInfo.content_type !== ContentType.tv_dvr_vod &&
      this.player.get(PLAYER_PROPERTY.hideNextPrevButtons)
    ) {
      this.player.set(PLAYER_PROPERTY.hideNextPrevButtons, false);
    }

    if (
      [
        ContentType.tv_dvr,
        ContentType.tv_dvr_catchup,
        ContentType.tv_dvr_vod,
      ].includes(videoInfo.content_type)
    ) {
      if (
        this.channelVideoInfo?.video_id !== videoInfo.video_id &&
        videoInfo.content_type !== ContentType.tv_dvr_vod
      ) {
        this.playingEntity = new PlayingEntity(videoInfo);
        this.setPluginState(PluginState.idle);

        switch (videoInfo.content_type) {
          case ContentType.tv_dvr:
            this.channelVideoInfo = videoInfo;
            break;
          case ContentType.tv_dvr_catchup:
            this.startCatchup = videoInfo;
            // get the channel's videoInfo
            this.requestStreamFuncs.getStream(
              videoInfo.video_id,
              (channelVideoInfo) => {
                if (channelVideoInfo) {
                  this.channelVideoInfo = channelVideoInfo;
                  if (this.isNeedForceLive) {
                    this.onBackOnAir({
                      data: { isRestrictedUI: true },
                      name: '',
                    });
                    this.isNeedForceLive = false;
                  }
                }
              }
            );
            break;
        }

        if (options?.switchFromStartedCatchup) {
          // hz what is this
          const timeOffset = this.player.get(
            PLAYER_PROPERTY.currentProgramTime
          );
          this.playingEntity.setTimeOffset(timeOffset);
          this.setPluginState(PluginState.timeshift);
        }
        subscribeOnEvents(this);
        const channel = this.player.get<IChannel | null>(
          PLAYER_PROPERTY.currentChannel
        );
        if (channel) {
          this.onCurrentChannelChange({ data: channel, name: '' });
        }
        this.player.on(
          PLAYER_EVENT.currentChannelChange,
          this.onCurrentChannelChange
        );
      }
    } else {
      this.disablePlugin();
    }
  };

  onCurrentChannelChange = (event: IEvent<IChannel>): void => {
    const channel = event.data;
    if (channel.type === ChannelType.DVR_CHANNEL && !channel.isLocked) {
      this.currentChannel = channel;
      const program = channel.currentProgram;
      if (program) {
        this.onCurrentProgramChanged({ data: program, name: '' });
      }
    }
  };

  onCurrentProgramChanged = (event: IEvent<IProgram | null>): void => {
    let program = event.data;
    if (
      program?.channel?.type === ChannelType.DVR_CHANNEL &&
      !program?.channel?.isLocked
    ) {
      switch (this.pluginState) {
        case PluginState.idle:
        case PluginState.onAir: {
          const { channel } = program;
          if (
            this.startCatchup?.start_timestamp &&
            this.startCatchup?.end_timestamp
          ) {
            if (
              !this.player.config.isDisableUpsaleSkin &&
              channel.isChannelControlsRestricted
            ) {
              this.startCatchup = null;
              if (!this.channelVideoInfo) {
                this.isNeedForceLive = true;
              } else {
                this.onBackOnAir({ data: { isRestrictedUI: true }, name: '' });
              }
            } else {
              const catchupProgram = findCatchupInChannel(
                channel,
                this.startCatchup.start_timestamp +
                  (this.startCatchup.end_timestamp -
                    this.startCatchup.start_timestamp) /
                    2
              );
              if (catchupProgram) {
                this.startCatchup = null;
                program = catchupProgram;
                this.setPluginState(PluginState.catchup);
              } else {
                const onProgramListUpdated = () => {
                  this.player.off(
                    PLAYER_EVENT.currentProgramListUpdated,
                    onProgramListUpdated
                  );
                  this.onCurrentProgramChanged({ data: program, name: '' });
                };

                this.player.on(
                  PLAYER_EVENT.currentProgramListUpdated,
                  onProgramListUpdated
                );
              }
            }
          } else {
            this.setPluginState(PluginState.onAir);
          }
          this.player.set(PLAYER_PROPERTY.playingProgram, program, true);
          this.player.set(
            PLAYER_PROPERTY.playingProgramTime,
            this.player.get(PLAYER_PROPERTY.currentProgramTime) || 0,
            true
          );
          this.player.set(
            PLAYER_PROPERTY.playingProgramDuration,
            program.duration,
            true
          );
          break;
        }

        case PluginState.timeshift:
          this.setPluginState(PluginState.catchup);
          break;
      }
    }
  };

  onChannelSwitch = (event: IEvent<IChannel>): void => {
    if (!('type' in event.data)) {
      // if event.data is {id: <channel_id>}
      const getChannelById = this.player.get('epg:getChannelById');
      if (typeof getChannelById === 'function') {
        const channel = getChannelById(
          (event.data as unknown as { id: string }).id
        );
        if (channel) {
          event.data = channel;
        }
      }
    }

    // need this check, because 'channel switch' could be a switch to the same channel but in the another genre group
    // e.g. from 'recommended' to 'favorite' or in the original genre
    if (this.channelVideoInfo?.video_id !== event.data.id) {
      this.channelVideoInfo = null;
    }

    if (
      this.currentChannel &&
      event.data &&
      this.currentChannel.id !== event.data.id
    ) {
      if (
        event.data.type === undefined ||
        event.data.type === ChannelType.REGULAR_CHANNEL ||
        event.data.type === ChannelType.VOD_CHANNEL ||
        event.data.isLocked
      ) {
        this.disablePlugin();
      }
    }
  };

  onSeek = (event: IEvent<number>): void => {
    const targetTime = event.data > 0 ? event.data : 0;
    if (this.pluginState === PluginState.catchup) {
      const playingProgram: IProgram | null = this.player.get(
        PLAYER_PROPERTY.playingProgram
      );
      if (playingProgram && this.playingEntity?.isTimeshiftUrl) {
        this.player.set(PLAYER_PROPERTY.playingProgramTime, targetTime);
        const now = this.player.get(PLAYER_PROPERTY.currentTimestamp);
        const timeOffset = now - (playingProgram.startTimestamp + targetTime);
        this.playingEntity.setTimeOffset(timeOffset);
        this.playVideoFunc(this.playingEntity);
      } else {
        this.player.seekTo(targetTime);
      }
    } else if (this.playingEntity) {
      // onAir or timeshift
      const currentProgramTime = this.player.get(
        PLAYER_PROPERTY.currentProgramTime
      );
      if (targetTime < currentProgramTime) {
        this.player.set(PLAYER_PROPERTY.playingProgramTime, targetTime);
        const timeOffset = currentProgramTime - targetTime;
        this.playingEntity.setTimeOffset(timeOffset);
        this.setPluginState(PluginState.timeshift);
      } else {
        this.player.set(PLAYER_PROPERTY.playingProgramTime, currentProgramTime);
        this.playingEntity.setTimeOffset(0);
        this.setPluginState(PluginState.onAir);
      }
      this.playVideoFunc(this.playingEntity);
    }
  };

  onBackOnAir = (
    event: IEvent<
      { startFromZero?: boolean; isRestrictedUI?: boolean } | undefined
    >
  ): void => {
    if (event.data?.isRestrictedUI) {
      this.player.emit('skin:restrictedUIButtonClick');
    }

    if (this.channelVideoInfo && this.currentChannel?.currentProgram) {
      const { currentProgram } = this.currentChannel;
      this.player.set(PLAYER_PROPERTY.playingProgram, currentProgram);
      this.player.set(
        PLAYER_PROPERTY.playingProgramDuration,
        currentProgram.duration
      );
      if (
        this.playingEntity &&
        this.playingEntity.isMPD &&
        this.playingEntity.videoInfo.src === this.channelVideoInfo.src
      ) {
        this.playingEntity.setTimeOffset(0);
      } else {
        this.playingEntity = new PlayingEntity(this.channelVideoInfo, {
          isForceAutoplay: true,
        });
      }

      const currentProgramTime = this.player.get(
        PLAYER_PROPERTY.currentProgramTime
      );
      const startFromZero = event.data?.startFromZero ?? false;
      if (startFromZero) {
        if (this.playingEntity.isMPD) {
          const startTimestamp =
            this.player.get(PLAYER_PROPERTY.currentTimestamp) -
            currentProgramTime;
          this.playingEntity.setStartSeconds(
            startTimestamp,
            currentProgramTime
          );
        } else {
          this.playingEntity.setTimeOffset(currentProgramTime);
        }
        this.setPluginState(PluginState.timeshift);
        this.player.set(PLAYER_PROPERTY.playingProgramTime, 0);
      } else {
        this.setPluginState(PluginState.onAir);
        this.player.set(PLAYER_PROPERTY.playingProgramTime, currentProgramTime);
      }
      this.playVideoFunc(this.playingEntity);
    }
  };

  onPlayCatchup = (event: IEvent<IProgram>): void => {
    const { data: program } = event;

    const onStream = (stream: IVideoInfo | null): void => {
      if (stream) {
        this.player.set(PLAYER_PROPERTY.hideNextPrevButtons, false);
        this.player.set(PLAYER_PROPERTY.playingProgram, program);
        this.player.set(
          PLAYER_PROPERTY.playingProgramDuration,
          program.duration
        );
        this.player.set(PLAYER_PROPERTY.playingProgramTime, 0);

        this.setPluginState(PluginState.catchup);
        this.isNeedToUpdateTimeoffset = false;
        this.playingEntity = new PlayingEntity(stream, {
          isForceAutoplay: true,
        });
        program.clickedFromProgramsPanel = false;
        if (stream.content_type === ContentType.tv_dvr_catchup) {
          program.virtualStream = stream;
        }

        this.playVideoFunc(this.playingEntity);
      }
    };

    if (program.virtualObjectId && program.state === ProgramState.PAST) {
      if (program.virtualStream) {
        onStream(program.virtualStream);
      } else if (this.currentChannel) {
        this.requestStreamFuncs.getVirtualStream(
          this.currentChannel.id,
          program.virtualObjectId,
          onStream
        );
      }
    } else if (program.objectId && program.objectId !== -1) {
      this.requestStreamFuncs.getStream(program.objectId, onStream);
    } else {
      // this is the case when 'rich epg' vod-program in past has ended
      // we got the next program, but this program doesn't have virutal object id and object
      // so got to live
      this.onBackOnAir({ data: undefined, name: '' });
    }
  };

  onStateChange = (event: IEvent<PlayerState>): void => {
    switch (event.data) {
      case PlayerState.paused:
        switch (this.pluginState) {
          case PluginState.onAir:
            this.setPluginState(PluginState.timeshift);
            this.isNeedToUpdateTimeoffset = true;
            break;
          case PluginState.timeshift:
            this.isNeedToUpdateTimeoffset = true;
            break;
          case PluginState.catchup:
            if (this.playingEntity?.isTimeshiftUrl) {
              this.isNeedToUpdateTimeoffset = true;
            }
        }
        break;

      case PlayerState.playing: {
        const now = this.player.get(PLAYER_PROPERTY.currentTimestamp);
        this.lastTimestamp = now;
        if (this.isNeedToUpdateTimeoffset && this.playingEntity) {
          // update timeOffset only if user paused the playback
          this.isNeedToUpdateTimeoffset = false;
          const playingProgram: IProgram | null = this.player.get(
            PLAYER_PROPERTY.playingProgram
          );
          if (playingProgram) {
            const playingTime = this.player.get(
              PLAYER_PROPERTY.playingProgramTime
            );
            const timeOffset =
              now - (playingProgram.startTimestamp + playingTime);
            this.playingEntity.setTimeOffset(timeOffset);
            this.playVideoFunc(this.playingEntity);
          }
        }
        break;
      }

      case PlayerState.ended:
        if (this.pluginState === PluginState.catchup) {
          const nextProgram = this.currentChannel?.getNextProgram(
            this.player.get(PLAYER_PROPERTY.playingProgram)
          );
          switch (nextProgram?.state) {
            case ProgramState.PAST:
              setTimeout(() => {
                // BELOW COMMENT FROM PREVIOUS VERSION
                // some code also subscribed on this event and it does something bad
                // so video get stucked if the 'onPlayDvrProgram' called on the same eventloop cycle
                this.onPlayCatchup({ data: nextProgram, name: '' });
              }, 0);
              break;
            case ProgramState.CURRENT:
              this.onBackOnAir({ data: { startFromZero: true }, name: '' });
              break;
            case ProgramState.FUTURE:
            // this should never happen
            // eslint-disable-next-line no-fallthrough
            default:
              this.onBackOnAir({ data: undefined, name: '' });
          }
        }
    }
  };

  onTimeUpdate = (): void => {
    if (this.pluginState !== PluginState.idle) {
      switch (this.pluginState) {
        case PluginState.onAir: {
          const currentProgramTime = this.player.get(
            PLAYER_PROPERTY.currentProgramTime
          );
          this.player.set(
            PLAYER_PROPERTY.playingProgramTime,
            currentProgramTime
          );
          break;
        }

        case PluginState.catchup:
        case PluginState.timeshift:
          if (
            this.playingEntity?.isTimeshiftUrl &&
            this.player.get(PLAYER_PROPERTY.state) === PlayerState.playing
          ) {
            const playingTime = this.player.get<number>(
              PLAYER_PROPERTY.playingProgramTime
            );
            const now = this.player.get<number>(
              PLAYER_PROPERTY.currentTimestamp
            );
            const playingProgram = this.player.get<IProgram | null>(
              PLAYER_PROPERTY.playingProgram
            );
            const newPlayingTime = playingTime + now - this.lastTimestamp;
            this.player.set(PLAYER_PROPERTY.playingProgramTime, newPlayingTime);
            this.lastTimestamp = now;
            if (playingProgram && newPlayingTime > playingProgram.duration) {
              const nextProgram =
                this.currentChannel?.getNextProgram(playingProgram);
              if (nextProgram) {
                this.player.set(PLAYER_PROPERTY.playingProgram, nextProgram);
                this.player.set(
                  PLAYER_PROPERTY.playingProgramDuration,
                  nextProgram.duration
                );
                this.player.set(PLAYER_PROPERTY.playingProgramTime, 0);
                if (nextProgram.state === ProgramState.CURRENT) {
                  this.setPluginState(PluginState.timeshift);
                }
              }
            }
          }
          break;
      }
    }
  };

  onPositionChanged = (event: IEvent<number>): void => {
    if (
      this.pluginState === PluginState.catchup &&
      !this.playingEntity?.isLiveSrc
    ) {
      this.player.set(PLAYER_PROPERTY.playingProgramTime, event.data);
    }
  };

  onDurationChanged = (event: IEvent<number>): void => {
    if (
      this.pluginState === PluginState.catchup &&
      !this.playingEntity?.isLiveSrc
    ) {
      this.player.set(PLAYER_PROPERTY.playingProgramDuration, event.data);
    }
  };

  disablePlugin = (): void => {
    this.resetPlugin();
    this.player.set(PLAYER_PROPERTY.dismissSeekToLiveAfterPause, false);
    unsubscribeFromEvents(this);
  };

  private onContentBlocked = (event: IEvent) => {
    if (event.data.status > 0) {
      this.disablePlugin();
    }
  };

  private resetPlugin = (): void => {
    this.pluginState = PluginState.idle;
    this.currentChannel = null;
    this.playingEntity = null;
    this.channelVideoInfo = null;
    this.lastTimestamp = 0;
    this.startCatchup = null;
    this.isNeedToUpdateTimeoffset = false;
    this.isNeedForceLive = false;
  };

  private setPluginState = (state: PluginState) => {
    this.pluginState = state;
    const playerProperty = [
      PluginState.onAir,
      PluginState.timeshift,
      PluginState.catchup,
    ].includes(state)
      ? state
      : null;
    this.player.set(PLAYER_PROPERTY.channelState, playerProperty);
  };
}
